/**
 * ProjectProductAttributeCellEditor
 *
 * Custom cell editor for attrib0, attrib1, attrib2 columns. Uses attributes cache
 * (keyed by itemType) to show Picker (codebook type 5) or TextField.
 * Commits both display value and _id via onMultiFieldChange where applicable.
 */

import React, { useCallback, useContext, useRef } from "react";
import { Picker, Item, TextField } from "@adobe/react-spectrum";
import type { CustomEditorProps } from "../../../ui/TreeGridComponent/types";
import type { AttributeData } from "../ProjectProduct/ProjectProductAddEdit";
import type { ProjectProductTreeRowData } from "./ProjectProductTreeComponent";

export const AttributesCacheContext = React.createContext<
  Record<string, AttributeData[]> | null
>(null);

export function useAttributesCache(): Record<string, AttributeData[]> | null {
  return useContext(AttributesCacheContext);
}

type AttributeKey = "attrib0" | "attrib1" | "attrib2";

function getAttributeData(
  attributesCache: Record<string, AttributeData[]> | null,
  itemType: string | undefined,
  columnKey: string
): AttributeData | null {
  if (!attributesCache || !itemType) return null;
  const attrs = attributesCache[itemType];
  if (!attrs) return null;
  const index = columnKey === "attrib0" ? 0 : columnKey === "attrib1" ? 1 : 2;
  return attrs[index] ?? null;
}

export function ProjectProductAttributeCellEditor(
  props: CustomEditorProps<ProjectProductTreeRowData>
) {
  const { value, row, column, onChange, onCommit, onCancel, onMultiFieldChange, error, shouldFocus = false } =
    props;
  const cache = useAttributesCache();
  const attrKey = column.key as AttributeKey;
  const idKey = `${attrKey}_id` as keyof ProjectProductTreeRowData;
  const itemType = row.data?.itemType;
  const attributeData = getAttributeData(cache, itemType, column.key);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onCommit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    },
    [onCommit, onCancel]
  );

  if (!attributeData) {
    return (
      <div ref={containerRef} onKeyDown={handleKeyDown}>
        <TextField
          width="100%"
          aria-label={column.header ?? attrKey}
          value={String(value ?? "")}
          onChange={(val) => {
            onChange(val);
            onMultiFieldChange?.({ [attrKey]: val } as Partial<ProjectProductTreeRowData>);
          }}
          onBlur={() => onCommit()}
          autoFocus={shouldFocus}
        />
      </div>
    );
  }

  if (Number(attributeData.type) === 5 && attributeData.codebooks?.length) {
    const selectedId = (row.data[idKey] as string) ?? "";
    return (
      <div ref={containerRef} onKeyDown={handleKeyDown}>
        <Picker
          width="100%"
          aria-label={column.header ?? attrKey}
          selectedKey={selectedId || undefined}
          onSelectionChange={(key) => {
            const id = key as string;
            const name = attributeData.codebooks?.find((c) => c.id === id)?.name ?? "";
            
            // Only commit if the value actually changed
            const valueChanged = name !== value || id !== selectedId;
            if (!valueChanged) {
              return;
            }
            
            onChange(name);
            onMultiFieldChange?.({
              [attrKey]: name,
              [idKey]: id,
            } as Partial<ProjectProductTreeRowData>);
            onCommit();
          }}
          autoFocus={shouldFocus}
        >
          {attributeData.codebooks.map((codebook) => (
            <Item key={codebook.id}>{codebook.name}</Item>
          ))}
        </Picker>
      </div>
    );
  }

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown}>
      <TextField
        width="100%"
        aria-label={column.header ?? attrKey}
        value={String(value ?? "")}
        onChange={(val) => {
          onChange(val);
          onMultiFieldChange?.({ [attrKey]: val } as Partial<ProjectProductTreeRowData>);
        }}
        onBlur={() => onCommit()}
        autoFocus={shouldFocus}
      />
    </div>
  );
}
